import {
  Stepper as MantineStepper,
  StepperProps as MantineStepperProps,
  StepperStepProps as MantineStepperStepProps,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';

import styles from './Stepper.module.css';
import { StepperPartialIcon } from './StepperPartialIcon';

export type StepperVariant = 'default' | 'ai';

export type StepperProps = Omit<
  MantineStepperProps,
  'color' | 'styles' | 'classNames' | 'style' | 'radius' | 'iconSize'
> & {
  variant?: StepperVariant;
  /** Center the steps row horizontally within the stepper */
  isCentered?: boolean;
};

export type StepperStepProps = Omit<
  MantineStepperStepProps,
  'color' | 'styles' | 'classNames' | 'style'
> & {
  isPartial?: boolean;
  isPartialAndRequired?: boolean;
  isNavigable?: boolean;
};

export type StepperLinkProps = Omit<StepperStepProps, 'isNavigable'> &
  Pick<NextLinkProps, 'href' | 'prefetch'>;

const stepperClassNames = {
  root: styles.root,
  steps: styles.steps,
  step: styles.step,
  stepIcon: styles.stepIcon,
  stepCompletedIcon: styles.stepCompletedIcon,
  stepBody: styles.stepBody,
  stepLabel: styles.stepLabel,
  separator: styles.separator,
  content: styles.content,
};

const completedIcon = <IconCheck size={16} stroke={2} />;
const partialIcon = <StepperPartialIcon />;

export function Stepper({
  variant = 'default',
  isCentered = false,
  children,
  ...props
}: StepperProps) {
  return (
    <MantineStepper
      unstyled
      classNames={stepperClassNames}
      completedIcon={completedIcon}
      data-variant={variant}
      data-centered={isCentered || undefined}
      {...props}
    >
      {children}
    </MantineStepper>
  );
}

function StepperStep({
  isNavigable = false,
  isPartial = false,
  isPartialAndRequired = false,
  ...props
}: StepperStepProps) {
  const shouldShowPartialIcon =
    (isPartial || isPartialAndRequired) && props.state === 'stepCompleted';

  return (
    <MantineStepper.Step
      {...props}
      data-navigable={isNavigable || undefined}
      data-partial={shouldShowPartialIcon || undefined}
      data-partial-required={
        (shouldShowPartialIcon && isPartialAndRequired) || undefined
      }
      icon={shouldShowPartialIcon ? partialIcon : props.icon}
      completedIcon={shouldShowPartialIcon ? partialIcon : props.completedIcon}
    />
  );
}

function StepperLink({ href, prefetch, ...props }: StepperLinkProps) {
  const linkProps = { component: NextLink, href, prefetch };

  return (
    <StepperStep
      {...props}
      {...linkProps}
      isNavigable
      // Mantine derives tabIndex from this; without it the anchor is unfocusable.
      allowStepClick
    />
  );
}

Stepper.displayName = 'Stepper';
Stepper.Step = StepperStep;
Stepper.Link = StepperLink;
Stepper.Completed = MantineStepper.Completed;
